import { Share } from '@capacitor/share';
import { isCapacitorNative } from './isCapacitorNative';

export type ShareContentResult = 'shared' | 'copied' | 'cancelled';

function errorBlob(error: unknown): string {
  if (error instanceof Error) {
    return [error.name, error.message].filter(Boolean).join(' ');
  }
  return String(error ?? '');
}

function isShareCancelled(error: unknown): boolean {
  const blob = errorBlob(error);
  return (
    (error instanceof Error && error.name === 'AbortError') ||
    /cancel|abort|dismiss|share canceled|share cancelled/i.test(blob)
  );
}

function isShareUnavailable(error: unknown): boolean {
  const blob = errorBlob(error);
  return /not implemented|UNIMPLEMENTED|plugin is not implemented|canShare/i.test(blob);
}

/** True when a system share sheet is available (Capacitor or Web Share API). */
export function canUseSystemShareSheet(): boolean {
  if (isCapacitorNative()) {
    return true;
  }
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/** Browser / desktop: WhatsApp Web (wa.me often launches the Desktop app instead). */
export function buildWhatsAppWebSendUrl(args: {
  phone?: string | null;
  text?: string | null;
}): string {
  const params = new URLSearchParams();
  const digits = (args.phone ?? '').replace(/\D/g, '');
  if (digits) {
    params.set('phone', digits);
  }
  const message = args.text?.trim() || '';
  if (message) {
    params.set('text', message);
  }
  const qs = params.toString();
  return qs ? `https://web.whatsapp.com/send?${qs}` : 'https://web.whatsapp.com/send';
}

/**
 * System share sheet — Capacitor on Android/iOS, Web Share API in supporting browsers.
 * Does not open WhatsApp directly; the user picks a target from the sheet.
 */
export async function shareContent(args: {
  title?: string;
  text?: string;
  url?: string;
  dialogTitle?: string;
}): Promise<ShareContentResult> {
  const title = args.title?.trim() || undefined;
  const text = args.text?.trim() || undefined;
  const url = args.url?.trim() || undefined;
  const clipboardPayload = [title, text, url].filter(Boolean).join('\n');

  if (isCapacitorNative()) {
    try {
      await Share.share({
        title,
        text: text || undefined,
        url,
        dialogTitle: args.dialogTitle,
      });
      return 'shared';
    } catch (error) {
      if (isShareCancelled(error)) {
        return 'cancelled';
      }
      if (!isShareUnavailable(error)) {
        // fall through to clipboard
      }
    }
  } else if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch (error) {
      if (isShareCancelled(error)) {
        return 'cancelled';
      }
    }
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText && clipboardPayload) {
    await navigator.clipboard.writeText(clipboardPayload);
    return 'copied';
  }

  return 'cancelled';
}

export async function copyShareText(text: string): Promise<boolean> {
  const payload = text.trim();
  if (!payload || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(payload);
    return true;
  } catch {
    return false;
  }
}

/** Prefer the WhatsApp app inside Capacitor; WhatsApp Web in browsers. */
export function buildWhatsAppChatUrl(phone: string, text?: string | null): string {
  const digits = phone.replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  const message = text?.trim() || '';
  if (isCapacitorNative()) {
    const q = message ? `&text=${encodeURIComponent(message)}` : '';
    return `whatsapp://send?phone=${digits}${q}`;
  }
  return buildWhatsAppWebSendUrl({ phone: digits, text: message || null });
}

/** Open WhatsApp chat — app on Capacitor, WhatsApp Web in the browser. */
export function openWhatsAppChat(phone: string, text?: string | null): void {
  const url = buildWhatsAppChatUrl(phone, text);
  if (!url) {
    return;
  }
  if (isCapacitorNative()) {
    window.location.assign(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Share listing text via WhatsApp Web (desktop) or app scheme (Capacitor). */
export function shareViaWhatsApp(text: string): void {
  const message = text.trim();
  if (!message) {
    return;
  }
  if (isCapacitorNative()) {
    window.location.assign(`whatsapp://send?text=${encodeURIComponent(message)}`);
    return;
  }
  window.open(buildWhatsAppWebSendUrl({ text: message }), '_blank', 'noopener,noreferrer');
}
